import { Component, effect, Signal } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { GenericComponent } from '../generic/generic.component';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { SidenavService } from 'src/app/shared/services/sidenav.service';
import { Store } from '@ngxs/store';
import { ConcessioncontrolState, PrepareDataFromKeys } from '../../states/concessioncontrol.state';
import { combineLatest, distinctUntilChanged } from 'rxjs';
import { CC_CONSTANTS, StrokeLineCap } from '../../assets/constants';
import {
    ApexAxisChartSeries,
    ApexDataLabels,
    ApexGrid,
    ApexPlotOptions,
    ApexTooltip,
    ApexXAxis,
    ApexYAxis,
} from 'ng-apexcharts';
import {
    colorAec1BlueLight,
    colorAec2BlueDark,
    colorAec4Orange,
    colorAec7RedLight,
    colorAec8RedDark,
} from 'src/app/shared/all.constants';
import { convertNumber, formatSeconds } from 'src/app/shared/all.helpers';
import {
    EDF_CUSTOMERS_QUALITY_BLUE_CONTRACT_RATE_CHART,
    EDF_CUSTOMERS_QUALITY_BLUE_CONTRACT_RATE_COMPARISON_CHART,
    EDF_CUSTOMERS_QUALITY_CTS_CLIENT_RATE_CHART,
    EDF_CUSTOMERS_QUALITY_KPIS,
    EDF_CUSTOMERS_QUALITY_PHONE_CALL_SUCCESS_RATE_CHART,
    EDF_CUSTOMERS_QUALITY_SELF_REPORT_BILLS_RATE_CHART,
} from '../../assets/keys';

const KEYS = [
    EDF_CUSTOMERS_QUALITY_KPIS,
    EDF_CUSTOMERS_QUALITY_PHONE_CALL_SUCCESS_RATE_CHART,
    EDF_CUSTOMERS_QUALITY_CTS_CLIENT_RATE_CHART,
    EDF_CUSTOMERS_QUALITY_SELF_REPORT_BILLS_RATE_CHART,
    EDF_CUSTOMERS_QUALITY_BLUE_CONTRACT_RATE_CHART,
    EDF_CUSTOMERS_QUALITY_BLUE_CONTRACT_RATE_COMPARISON_CHART,
];

interface QualityKpiData {
    organizationId?: string;
    year?: string;
    error?: boolean;

    successPhoneCallRate?: number;
    successPhoneCallRateEvolution?: number;

    monthlySubscriptionRate?: number;
    monthlySubscriptionRateEvolution?: number;
}

interface CallSuccessChartData {
    organizationId?: string;
    year?: string;
    error?: boolean;

    xYear?: number;
    callSuccessRate?: number;
    averageWaitingTime?: number;
}

interface CtsClientChartData {
    organizationId?: string;
    year?: string;
    error?: boolean;

    xYear?: number;
    ctsClientRate?: number;
}

interface SelfReportBillsChartData {
    organizationId?: string;
    year?: string;
    error?: boolean;

    xYear?: number;
    selfReportBillsRate?: number;
}

interface BlueContractChartData {
    organizationId?: string;
    year?: string;
    error?: boolean;

    xYear?: number;
    automaticPayment?: number;
    monthlyPayment?: number;
}

interface BlueContractComparisonChartData {
    organizationId?: string;
    year?: string;
    error?: boolean;

    category?: string;
    min?: number;
    max?: number;
    mean?: number;
    clientValue?: number;
}

@UntilDestroy()
@Component({
    selector: 'app-edf-quality',
    templateUrl: './edf-quality.component.html',
    styleUrls: ['./edf-quality.component.scss'],
})
export class EdfQualityComponent extends GenericComponent {
    public texts = CC_CONSTANTS.edfQuality.texts;

    public kpis: Signal<QualityKpiData[]> = this.store.selectSignal(
        ConcessioncontrolState.getDataByKey(EDF_CUSTOMERS_QUALITY_KPIS),
    ) as Signal<QualityKpiData[]>;

    public callSuccesschartData: Signal<CallSuccessChartData[]> = this.store.selectSignal(
        ConcessioncontrolState.getDataByKey(EDF_CUSTOMERS_QUALITY_PHONE_CALL_SUCCESS_RATE_CHART),
    ) as Signal<CallSuccessChartData[]>;
    public callSuccessChartSeries: ApexAxisChartSeries = [];
    public callSuccessChartError: boolean = true;
    public callSuccessDataLabels: ApexDataLabels = {
        enabled: true,
        enabledOnSeries: [],
        formatter: this._classSuccessRateFormatter,
    };
    public callSuccessXaxisCategories: ApexXAxis['categories'];
    public callSuccessYaxis: ApexYAxis[] = [
        {
            seriesName: this.texts.phoneCallSuccessRateChart.series[0].name,
            min: 0,
            max: 100,
            title: {
                text: this.texts.phoneCallSuccessRateChart.yaxisTitleLeft,
            },
        },
        {
            seriesName: this.texts.phoneCallSuccessRateChart.series[1].name,
            min: 0,
            opposite: true,
            title: {
                text: this.texts.phoneCallSuccessRateChart.yaxisTitleRight,
            },
        },
    ];
    public callSuccessCustomTooltip: ApexTooltip['custom'];

    public ctsClientchartData: Signal<CtsClientChartData[]> = this.store.selectSignal(
        ConcessioncontrolState.getDataByKey(EDF_CUSTOMERS_QUALITY_CTS_CLIENT_RATE_CHART),
    ) as Signal<CtsClientChartData[]>;
    public ctsClientChartSeries: ApexAxisChartSeries = [];
    public ctsClientChartError: boolean = true;
    public ctsClientDataLabels: ApexDataLabels = {
        enabled: true,
        enabledOnSeries: [],
    };
    public ctsClientXaxisCategories: ApexXAxis['categories'];
    public ctsClientYaxis: ApexYAxis[] = [
        {
            seriesName: this.texts.edfCtsClientRateChart.ctsClientRateTitle,
            min: 0,
            title: {
                text: this.texts.edfCtsClientRateChart.yaxisTitleLeft,
            },
        },
    ];

    public selfReportBillschartData: Signal<SelfReportBillsChartData[]> = this.store.selectSignal(
        ConcessioncontrolState.getDataByKey(EDF_CUSTOMERS_QUALITY_SELF_REPORT_BILLS_RATE_CHART),
    ) as Signal<SelfReportBillsChartData[]>;
    public selfReportBillsChartSeries: ApexAxisChartSeries = [];
    public selfReportBillsChartError: boolean = true;
    public selfReportBillsDataLabels: ApexDataLabels = {
        enabled: true,
        enabledOnSeries: [],
    };
    public selfReportBillsXaxisCategories: ApexXAxis['categories'];
    public selfReportBillsYaxis: ApexYAxis[] = [
        {
            seriesName: this.texts.edfSelfReportBillsRateChart.selfReportBillsRateTitle,
            min: 0,
            title: {
                text: this.texts.edfSelfReportBillsRateChart.yaxisTitleLeft,
            },
        },
    ];

    public blueContractchartData: Signal<BlueContractChartData[]> = this.store.selectSignal(
        ConcessioncontrolState.getDataByKey(EDF_CUSTOMERS_QUALITY_BLUE_CONTRACT_RATE_CHART),
    ) as Signal<BlueContractChartData[]>;
    public blueContractChartSeries: ApexAxisChartSeries = [];
    public blueContractChartError: boolean = true;
    public blueContractDataLabels: ApexDataLabels = {
        enabled: true,
        enabledOnSeries: [],
    };
    public blueContractXaxisCategories: ApexXAxis['categories'];
    public blueContractYaxis: ApexYAxis[] = [
        {
            seriesName: [
                this.texts.edfBlueContractRateChart.monthlyPaymentTitle,
                this.texts.edfBlueContractRateChart.automaticPaymentTitle,
            ],
            min: 0,
            title: {
                text: this.texts.edfBlueContractRateChart.yaxisTitleLeft,
            },
        },
    ];
    public blueContractGrid: ApexGrid = { padding: { left: 30 } };

    public blueContractRateComparisonChartSeries: ApexAxisChartSeries = [];
    public blueContractRateComparisonChartError: boolean = true;
    public blueContractRateComparisonChartDataSignal: Signal<BlueContractComparisonChartData[]> =
        this.store.selectSignal(
            ConcessioncontrolState.getDataByKey(
                EDF_CUSTOMERS_QUALITY_BLUE_CONTRACT_RATE_COMPARISON_CHART,
            ),
        ) as Signal<BlueContractComparisonChartData[]>;
    public blueContractRateComparisonCustomTooltip: ApexTooltip['custom'];
    public blueContractRateChartYaxis: ApexYAxis[] = [
        {
            seriesName: 'Serie 1',
            min: 0,
            max: 100,
        },
    ];

    public comparisonDataLabels: ApexDataLabels = {
        enabled: false,
    };
    public comparisonPlotOptions: ApexPlotOptions = {
        bar: {
            horizontal: true,
            barHeight: this.setRangeBarHeight(2),
            borderRadius: 1,
        },
    };

    constructor(
        private readonly route: ActivatedRoute,
        private readonly sidenavService: SidenavService,
        private readonly store: Store,
    ) {
        super(route, sidenavService, store);

        effect(() => {
            const callSuccesschartData = this.callSuccesschartData();
            this.callSuccessChartError = this.checkForError(callSuccesschartData, [
                'xYear',
                'callSuccessRate',
                'averageWaitingTime',
            ]);
            if (!this.callSuccessChartError) {
                this.callSuccessXaxisCategories = callSuccesschartData.map((data) => data.xYear);
                this.callSuccessChartSeries = [
                    {
                        name: this.texts.phoneCallSuccessRateChart.series[0].name,
                        type: 'column',
                        data: callSuccesschartData.map((data) => data.callSuccessRate!),
                        color: colorAec1BlueLight,
                    },
                    {
                        name: this.texts.phoneCallSuccessRateChart.series[1].name,
                        type: 'line',
                        data: callSuccesschartData.map((data) => data.averageWaitingTime!),
                        color: colorAec7RedLight,
                    },
                ];
                this.callSuccessDataLabels.enabledOnSeries = [0, 1];
            }

            const ctsClientchartData = this.ctsClientchartData();
            this.ctsClientChartError = this.checkForError(ctsClientchartData, [
                'xYear',
                'ctsClientRate',
            ]);
            if (!this.ctsClientChartError) {
                this.ctsClientXaxisCategories = ctsClientchartData.map((data) => data.xYear);
                this.ctsClientChartSeries = [
                    {
                        name: this.texts.edfCtsClientRateChart.ctsClientRateTitle,
                        type: 'column',
                        data: ctsClientchartData.map((data) => data.ctsClientRate!),
                        color: colorAec2BlueDark,
                    },
                ];
                this.ctsClientDataLabels.enabledOnSeries = [0];
            }

            const selfReportBillschartData = this.selfReportBillschartData();
            this.selfReportBillsChartError = this.checkForError(selfReportBillschartData, [
                'xYear',
                'selfReportBillsRate',
            ]);
            if (!this.selfReportBillsChartError) {
                this.selfReportBillsXaxisCategories = selfReportBillschartData.map(
                    (data) => data.xYear,
                );
                this.selfReportBillsChartSeries = [
                    {
                        name: this.texts.edfSelfReportBillsRateChart.selfReportBillsRateTitle,
                        type: 'column',
                        data: selfReportBillschartData.map((data) => data.selfReportBillsRate!),
                        color: colorAec2BlueDark,
                    },
                ];
                this.selfReportBillsDataLabels.enabledOnSeries = [0];
            }

            const blueContractchartData = this.blueContractchartData();
            this.blueContractChartError = this.checkForError(blueContractchartData, [
                'xYear',
                'monthlyPayment',
                'automaticPayment',
            ]);
            if (!this.blueContractChartError) {
                this.blueContractXaxisCategories = blueContractchartData.map((data) => data.xYear);
                this.blueContractChartSeries = [
                    {
                        name: this.texts.edfBlueContractRateChart.monthlyPaymentTitle,
                        type: 'line',
                        data: blueContractchartData.map((data) => data.monthlyPayment!),
                        color: colorAec1BlueLight,
                    },
                    {
                        name: this.texts.edfBlueContractRateChart.automaticPaymentTitle,
                        type: 'line',
                        data: blueContractchartData.map((data) => data.automaticPayment!),
                        color: colorAec2BlueDark,
                    },
                ];
                this.blueContractDataLabels.enabledOnSeries = [0, 1];
            }
            const blueContractRateComparisonChartData =
                this.blueContractRateComparisonChartDataSignal();
            this.blueContractRateComparisonChartError = this.checkForError(
                blueContractRateComparisonChartData,
                ['category', 'min', 'max', 'mean'],
            );
            if (!this.blueContractRateComparisonChartError) {
                this.blueContractRateComparisonChartSeries = [
                    {
                        name: this.texts.edfBlueContractRateComparisonChart.serieLabel,
                        data: blueContractRateComparisonChartData.map((data) => ({
                            x: data.category,
                            y: [data.min, data.max],
                            goals: [
                                {
                                    name: this.texts.edfBlueContractRateComparisonChart
                                        .aecMeanLabel,
                                    value: data.mean!,
                                    strokeWidth: 10,
                                    strokeHeight: 0,
                                    strokeLineCap: StrokeLineCap.ROUND,
                                    strokeColor: colorAec8RedDark,
                                },
                                {
                                    name: this.texts.edfBlueContractRateComparisonChart
                                        .clientValueLabel,
                                    value: data.clientValue!,
                                    strokeWidth: 10,
                                    strokeHeight: 0,
                                    strokeLineCap: StrokeLineCap.ROUND,
                                    strokeColor: colorAec1BlueLight,
                                },
                            ],
                        })),
                        color: colorAec4Orange,
                    },
                    {
                        name: this.texts.edfBlueContractRateComparisonChart.serieLabelAvg,
                        type: 'bar',
                        data: [],
                        color: colorAec8RedDark,
                    },
                    {
                        name: this.texts.edfBlueContractRateComparisonChart.serieLabelValue,
                        type: 'bar',
                        data: [],
                        color: colorAec1BlueLight,
                    },
                ];
            }
        });
    }

    ngOnInit() {
        // Every time currentOrganization or currentYear changes, we tell the store he has to ask for the data
        combineLatest([this._currentOrganization$, this._currentYear$])
            .pipe(untilDestroyed(this), distinctUntilChanged())
            .subscribe(() => {
                this.store.dispatch(new PrepareDataFromKeys({ keys: KEYS }));
            });

        this.callSuccessCustomTooltip = this._setCallSuccessCustomTooltip();
        this.blueContractRateComparisonCustomTooltip =
            this.setblueContractRateComparisonCustomTooltip(
                this.texts.edfBlueContractRateComparisonChart.precision,
            );
    }

    private _classSuccessRateFormatter(value: number, opts: { seriesIndex: number }): string {
        return opts.seriesIndex === 0 ? convertNumber(value, null) : formatSeconds(value);
    }

    private _setCallSuccessCustomTooltip(): ApexTooltip['custom'] {
        return ({ series, seriesIndex, dataPointIndex, w }) => {
            const collapsedSeriesIndices = w.globals.collapsedSeriesIndices;

            const categoryLabels = w.globals.categoryLabels;
            const title = categoryLabels[dataPointIndex];

            const precision = this.texts.phoneCallSuccessRateChart.series[0].precision;
            const callSuccessSerie = w.globals.initialSeries[0];
            const {
                name: callSuccessName,
                color: callSuccessColor,
                valueStringified: callSuccessStringified,
            } = this.setCustomTooltipData(precision, callSuccessSerie, dataPointIndex);

            const waitingTimeSerie = w.globals.initialSeries[1];
            const waitingTimeName = waitingTimeSerie.name;
            const waitingTimeColor = waitingTimeSerie.color;
            const waitingTimePoint = waitingTimeSerie.data[dataPointIndex];
            const waitingTimePointStringified = formatSeconds(waitingTimePoint);

            const dataTooltips = [
                {
                    color: callSuccessColor,
                    name: callSuccessName,
                    value: callSuccessStringified + ' %',
                },
                {
                    color: waitingTimeColor,
                    name: waitingTimeName,
                    value: waitingTimePointStringified,
                },
            ];
            const html = this.setCustomTooltip(collapsedSeriesIndices, title, dataTooltips);
            return html;
        };
    }

    public setblueContractRateComparisonCustomTooltip(
        precision: number | null = null,
    ): ApexTooltip['custom'] {
        return function ({ series, seriesIndex, dataPointIndex, w }) {
            const dataSerie = w.globals.initialSeries[seriesIndex];
            const serieName = dataSerie.name;
            const serieColor = dataSerie.color;
            const dataPoint = dataSerie.data[dataPointIndex];
            const min = convertNumber(dataPoint.y[0], precision, false);
            const max = convertNumber(dataPoint.y[1], precision, false);

            const meanGoal = dataPoint.goals[0];
            const meanValue = convertNumber(meanGoal.value, precision, false);
            const meanName = meanGoal.name;
            const meanColor = meanGoal.strokeColor;

            const clientValueGoal = dataPoint.goals[1];
            const clientValue = convertNumber(clientValueGoal.value, precision, false);
            const clientValueName = clientValueGoal.name;
            const clientColor = clientValueGoal.strokeColor;

            return `
                <div class="apexcharts-tooltip-title">
                    ${dataPoint.x}
                </div>

                <div class="apexcharts-tooltip-series-group apexcharts-active" style="order: 1;display: flex;">
                    <span class="apexcharts-tooltip-marker" style="background-color: ${serieColor}"></span>
                    <div class="apexcharts-tooltip-text">
                        <div class="apexcharts-tooltip-y-group">
                            <span class="apexcharts-tooltip-text-y-label">${serieName}:</span>
                            <span class="apexcharts-tooltip-text-y-value"> ${min} - ${max} %</span>
                        </div>
                         <div class="apexcharts-tooltip-goals-group">
                         </div>
                    </div>
                </div>
                <div class="apexcharts-tooltip-series-group apexcharts-active" style="order: 2;display: flex;">
                    <span class="apexcharts-tooltip-marker" style="background-color: ${meanColor}"></span>
                    <div class="apexcharts-tooltip-text">
                        <div class="apexcharts-tooltip-y-group">
                            <span class="apexcharts-tooltip-text-y-label">${meanName}:</span>
                            <span class="apexcharts-tooltip-text-y-value"> ${meanValue} %</span>
                        </div>
                         <div class="apexcharts-tooltip-goals-group">
                         </div>
                    </div>
                </div>
                 <div class="apexcharts-tooltip-series-group apexcharts-active" style="order: 3;display: flex;">
                    <span class="apexcharts-tooltip-marker" style="background-color: ${clientColor}"></span>
                    <div class="apexcharts-tooltip-text">
                        <div class="apexcharts-tooltip-y-group">
                            <span class="apexcharts-tooltip-text-y-label">${clientValueName}:</span>
                            <span class="apexcharts-tooltip-text-y-value"> ${clientValue} %</span>
                        </div>
                    </div>
                </div>
            `;
        };
    }
}
